import { Component } from '@theme/component';

/**
 * Filters the product cards of the collection grid on the page by title while the shopper types.
 * Cards that don't match get the `hidden` attribute; when nothing matches, the status line shows
 * the section's empty text with [query] replaced by the search text.
 * Without JavaScript the form submits to the store search instead.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input - The search field.
 * @property {HTMLElement} status - The live region for the empty message.
 *
 * @extends Component<Refs>
 */
class PawwjiCollectionSearch extends Component {
  requiredRefs = ['input', 'status'];

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #frame = null;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new MutationObserver(this.#scheduleFilter);
    this.#observer.observe(document.querySelector('results-list') ?? document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });

    if (this.refs.input.value.trim()) this.#filter();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  /**
   * Filters the cards as the search text changes.
   */
  handleInput = () => {
    this.#filter();
  };

  /**
   * Keeps the shopper on the page: the cards are already filtered as they type.
   * @param {SubmitEvent} event
   */
  handleSubmit = (event) => {
    event.preventDefault();
    this.#filter();
  };

  /**
   * Re-applies the filter once per frame when the grid re-renders (filters, sorting, more results).
   */
  #scheduleFilter = () => {
    if (this.#frame !== null) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#filter();
    });
  };

  /**
   * @returns {HTMLElement[]} The product grid items on the page.
   */
  #items() {
    return [...document.querySelectorAll('.product-grid .product-grid__item')].filter(
      (item) => item instanceof HTMLElement
    );
  }

  #filter() {
    const query = this.refs.input.value.trim().toLowerCase();
    let visibleCount = 0;
    let itemCount = 0;

    for (const item of this.#items()) {
      itemCount += 1;
      const title = item.querySelector('.product-card__link')?.textContent?.trim().toLowerCase() ?? '';
      const matches = !query || title.includes(query);

      if (item.hidden === matches) item.hidden = !matches;
      if (matches) visibleCount += 1;
    }

    const { status } = this.refs;
    const emptyText = this.dataset.emptyText ?? '';
    const message = query && itemCount > 0 && visibleCount === 0 && emptyText
      ? emptyText.replaceAll('[query]', this.refs.input.value.trim())
      : '';

    if (status.textContent !== message) status.textContent = message;
  }
}

if (!customElements.get('pawwji-collection-search')) {
  customElements.define('pawwji-collection-search', PawwjiCollectionSearch);
}
